/* eslint-disable react/display-name */
import { VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ComponentType } from '../../../../concepts/objects';
import { IsolatedObjectManager } from '../../../../concepts/objects/IsolatedObjectManager';
import { Icon } from '../../../../concepts/resources/Icon';
import { DropButton } from '../../../../ui/DropButton';
import { Physical } from '../../../canvas/entities/Physical';
import { Copy, FlexFrame, Spacer, Title, ToggleFrame, ToggleLabel } from '../../../dashboard/elements/LayoutElements';
import { Popup } from '../../../popups/Popup';
import { AutoScene } from '../Scene';
import { MappingTarget, TabularData, TabularDataDimension, TabularImportParams, TabularImportStepProps } from '../TabularImportModel';
import { Box, Button, Colors, Input, Select, Toggle } from './../../../../ui';

export interface TabularImportMappingStepConfig<T extends Physical = Physical> {
    type: ComponentType<T>;
    typeLabel: string;
    targets: MappingTarget[];
    preview: [number, number];
}

const FlexPane = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 50%;
    &:first-child {
        padding-right: .5rem;
    }
    &:last-child {
        padding-left: .5rem;
    }
`;

type HeaderToggleProps = Pick<TabularImportStepProps, 'params'> & { onUpdate: VoidCallback1<TabularImportParams> };
const HeaderToggle = React.memo<HeaderToggleProps>(({ params, onUpdate }) => {

    const [value, setValue] = useState(params.hasHeaders);
    const onToggle = useCallback(() => {
        setValue(!value);
        onUpdate({ ...params, hasHeaders: !value });
    }, [value, params, onUpdate]);

    return <ToggleFrame>
        <Toggle value={value} onAction={onToggle} />
        <ToggleLabel>First row contains header names</ToggleLabel>
    </ToggleFrame>;
});

const MappingSelectItemPreview = styled.span`
    margin-left: .5rem;
    color: ${Colors.gray400};
    font-style: italic;
`;

type MappingSelectorProps = {
    options: TabularDataDimension[],
    target: MappingTarget,
    value?: TabularDataDimension,
    hasHeaders?: boolean,
    onChange: VoidCallback1<TabularDataDimension>,
};

const MappingSelector = React.memo<MappingSelectorProps>(({ options, target, value, hasHeaders, onChange }) => {

    const adapter = {
        key: (d: any) => d.key,
        label: (d: any) => !hasHeaders ? d.keyLabel : d.label,
    };

    return <>
        <Input.Label>{target.label}</Input.Label>
        <Select adapter={adapter} items={options} selected={value} onChange={onChange} renderItems={
            (items, selected, onSelect) => items.map((tm, i) =>
                <Select.MenuItem key={tm.key} onClick={onSelect(tm) as any}>
                    {!hasHeaders ? tm.keyLabel : tm.label}
                    {!hasHeaders && <MappingSelectItemPreview>({tm.label})</MappingSelectItemPreview>}
                    {tm.key === selected?.key && <Select.MenuItemCheck />}
                </Select.MenuItem>
            )
        } />
    </>;
});

type MappingFormProps =
    Pick<TabularImportStepProps, 'data' | 'params'> &
    { config: TabularImportMappingStepConfig, onUpdate: VoidCallback1<TabularImportParams> };

const MappingForm = React.memo<MappingFormProps>(({ config, data, params, onUpdate }) => {

    const lookup = useCallback((t: MappingTarget) => params.mappings?.get(t) as TabularDataDimension, [params]);

    const onMap = useCallback((t: MappingTarget, s: TabularDataDimension) => {
        const mappings = new Map(params.mappings).set(t, s);
        onUpdate({ ...params, mappings });
    }, [params, onUpdate]);

    const dimensions = useMemo(() => TabularDataDimension.from(data), [data]);

    return <>
        {config.targets.map(t => <Box key={t.id}>
            <MappingSelector options={dimensions} hasHeaders={params.hasHeaders} target={t} value={lookup(t)} onChange={x => onMap(t, x)} />
            <Spacer f={1} />
        </Box>)}
    </>;
});

const PreviewFrame = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const PreviewScene = styled.div`
    position: relative;
    flex: 1 1 auto;

    ${DropButton} {
        position: absolute;
        top: 50%;        
        transform: translate(0, -50%);
        color: ${Colors.primaryText};
        
        &.--left { left: .5rem; }
        &.--right { right: .5rem; }
    }
`;

type MappingPreviewProps = Pick<TabularImportStepProps, 'data' | 'params'> & { config: TabularImportMappingStepConfig };
const MappingPreview = React.memo<MappingPreviewProps>(({ config, data, params }) => {

    const [page, setPage] = useState(0);

    const items = params.hasHeaders ? data.slice(1) : data;
    const total = items.length;
    const pageSize = config.preview[0] * config.preview[1];
    const pageCount = Math.ceil(total / pageSize);
    const index = (page * pageSize) % total;
    const slice = items.slice(index, index + pageSize);

    const iom = new IsolatedObjectManager();
    const figures = [] as any[];

    for (let i = 0; i < slice.length; i++) {
        const x = i % config.preview[0];
        const y = Math.floor(i / config.preview[0]);
        const f = iom.create(config.type);
        f.moveBy(f.size().multiply([x, y]).add(20));
        for (const [t, s] of params.mappings ?? []) {
            f.set(t.path, t.filter(slice[i][s.key]));
        }
        figures.push(f);
    }

    return <PreviewFrame>
        <Title>Preview ({page + 1} of {pageCount})</Title>
        <Copy>Your {total} {config.typeLabel} will look like this:</Copy>
        <PreviewScene>
            <AutoScene objects={figures} />
            <DropButton rounding="full" className="--left" onClick={() => setPage(x => x - 1)}>
                <Icon size="2" src="deqo://icons/hero/chevron-left" />
            </DropButton>
            <DropButton rounding="full" className="--right" onClick={() => setPage(x => x + 1)}>
                <Icon size="2" src="deqo://icons/hero/chevron-right" />
            </DropButton>
        </PreviewScene>
    </PreviewFrame>;
});

export const TabularImportMappingStep = <T extends Physical>(config: TabularImportMappingStepConfig<T>) =>
    React.memo<TabularImportStepProps>(({ data, params, onStep }) => {

        const paramsWithDefaults = useMemo(
            () => defaultFirstMappingToFirstDimension(data, config.targets, params),
            [data, config.targets, params]
        );

        const [state, setState] = useState(paramsWithDefaults);

        const onNext = useCallback(() => onStep(state, + 1), [onStep, state]);
        const onBack = useCallback(() => onStep(params, - 1), [onStep, params]);

        return <>
            <Popup.Content>
                <FlexFrame flex="row">
                    <FlexPane>
                        <Title>Mappings</Title>
                        <Copy>Map the columns of your data to {config.typeLabel} properties.</Copy>
                        <Spacer f={2} />
                        <HeaderToggle params={state} onUpdate={setState} />
                        <Spacer f={3} />
                        <MappingForm config={config} data={data} params={state} onUpdate={setState} />
                    </FlexPane>
                    <FlexPane>
                        <MappingPreview config={config} data={data} params={state} />
                    </FlexPane>
                </FlexFrame>
            </Popup.Content>
            <Popup.Actions>
                <Button onAction={onBack}>
                    Back
                </Button>
                <Button variant="primary" onAction={onNext}>
                    Next
                </Button>
            </Popup.Actions>
        </>;
    });

const defaultFirstMappingToFirstDimension = (data: TabularData, targets: MappingTarget[], params: TabularImportParams) => {
    const mappings = new Map<MappingTarget, TabularDataDimension>(params.mappings);
    if (mappings.size) return params;
    const dimensions = TabularDataDimension.from(data);
    if (!dimensions.length) return params;
    mappings.set(targets[0], dimensions[0]);
    return { ...params, mappings };
};
